import { WorldEventViewSchema } from '../../ws/protocol';
import { LotRaidSchema } from '../../models/lot';
import { CoalitionStateSchema } from '../../models/coalition';
import { ToneRulesViewSchema, ToneStateSchema } from '../../models/tone';
import { CompanyRankSchema } from '../../models/company';
import { z } from 'zod';
import { ReputationBandSchema } from '../../models/reputation';
import { ItemIdSchema } from '../../models/items';
import { WorldLayoutIdSchema } from '../../models/place';
import { PlayerSchema } from '../../models/player';
import {
  CraftingRulesSchema,
  StructureCatalogSchema,
  StructureDefIdSchema,
} from '../../models/town';
import { TimestampMsSchema } from '../../time';
import { CatalogHashSchema, HOTBAR_SLOTS, PROTOCOL_VERSION } from '../../ws/protocol';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/** A character's item bar (ITEM-CONSUME): slot 1 first; null is an empty slot. */
export const HotbarSchema = z.array(ItemIdSchema.nullable()).length(HOTBAR_SLOTS);

/**
 * Puts a consumable in a slot of the character's item bar, or empties it (`item: null`)
 * (ITEM-CONSUME). Any time, in or out of combat: it only says what each key uses. Setting the
 * same value again changes nothing, so a retry is harmless.
 */
export const putHotbarSlot = defineEndpoint({
  method: 'PUT',
  path: `${API_PREFIX}/me/hotbar`,
  access: 'session',
  body: z.object({
    slot: z.number().int().min(1).max(HOTBAR_SLOTS),
    item: ItemIdSchema.nullable(),
  }),
  response: z.object({ hotbar: HotbarSchema }),
});

/**
 * What every client of a server gets alike (NET-CLIENT-CACHE C2, ADR 0070): the definitions and
 * rules built from its resolved config and its layout, the same for everyone until it restarts with
 * another one. Its hash (`catalogHashOf`) travels in `welcome` and the initial state; the client
 * keeps it on disk by hash and asks for it again only when the hash changes.
 */
export const CatalogSchema = z.object({
  /** Structure definitions (CORE-ENTITY): build palette, panels by component, footprints. */
  structures: StructureCatalogSchema,
  craftingRules: CraftingRulesSchema,
  /** Every fragment item: its category and body cost (MAGIC-FRAGMENT), for the station. */
  fragments: z.record(
    ItemIdSchema,
    z.object({
      tier: z.enum(['low', 'medium', 'high']),
      bodyCost: z.number().int().positive(),
      technique: z.string(),
    }),
  ),
  toneRules: ToneRulesViewSchema,
  /**
   * What its client shows a broken piece and its repair with (SIEGE H2a): the shared
   * `integrityMax` and `repairCost` read these; the server still judges.
   */
  siegeRules: z.object({
    breach: z.object({ perCostUnit: z.record(z.enum(['wood', 'stone', 'metal']), z.number()) }),
    repair: z.object({
      costShare: z.number(),
      patchLoss: z.number(),
      patchFloor: z.number(),
    }),
    /**
     * The machines a siege camp assembles (SIEGE H2b, SI-2), by id: the parts each takes and
     * the object it makes, for the camp's panel to offer; the server still judges.
     */
    machines: z.record(
      z.string(),
      z.object({
        parts: z.record(ItemIdSchema, z.number().int().positive()),
        /** The object it makes (the ram), or the engine it stands on the camp's yard (H2b2). */
        item: ItemIdSchema.optional(),
        structure: StructureDefIdSchema.optional(),
      }),
    ),
  }),
  /**
   * What a character's contract may say (ECON-CONTRACT C3a), for the form to offer; the server
   * still judges it: its least coin pay, its listing fee (JC-2: paid always, a share of the coins
   * never below `minFee`), its takers' terms in game time, the most a guarantee may ask against
   * the pay, how many one keeps open.
   */
  contracts: z
    .object({
      minCoins: z.number().int().positive(),
      feeShare: z.number().min(0),
      minFee: z.number().int().min(0),
      termMs: z.object({ min: z.number().int().positive(), max: z.number().int().positive() }),
      guaranteeShare: z.number().min(0),
      maxOpen: z.number().int().positive(),
      /** The creature families a bounty may name (C3b). */
      families: z.array(z.string()).optional(),
    })
    .optional(),
  /**
   * Every kit (plan C8): the structure it pitches (a temporary camp), for the backpack to offer
   * pitching it; the server still judges where.
   */
  kits: z.record(ItemIdSchema, z.object({ structure: StructureDefIdSchema })),
  /**
   * The presages' rules (WORLD-PRESAGE): how far each degree is felt (`reach`, degree 1 first: what
   * a presage fades with as it nears that), and the metres a point is rounded to (`grain`).
   */
  presage: z.object({
    reach: z.tuple([z.number().positive(), z.number().positive(), z.number().positive()]),
    grain: z.number().int().positive(),
  }),
  /**
   * Every gunpowder charge (SIEGE GP-1..4, the item's `blast`): how long setting it and putting
   * its fuse out take, for the prompts and the bars; the server still judges each.
   */
  blasts: z.record(
    ItemIdSchema,
    z.object({ setMs: z.number().int().positive(), defuseMs: z.number().int().positive() }),
  ),
  /**
   * Every ancient crystal (CRAFT-MASTER MN-1): the least rank of the craftsman it wakes into a
   * master, for the workers' panel to offer it; the server still judges each.
   */
  crystals: z.record(ItemIdSchema, z.object({ minRank: z.number().int().nonnegative() })),
  /**
   * Every consumable item (ITEM-CONSUME): how long it takes and the shared cooldown it starts, for
   * the bar to show; the server still decides each use.
   */
  consumables: z.record(
    ItemIdSchema,
    z.object({
      useMs: z.number().int().positive(),
      cooldown: z.object({ group: z.string(), ms: z.number().int().positive() }).optional(),
    }),
  ),
  /**
   * Concealment (ADR 0050): how far a body no light reaches is seen at night, in metres, for
   * the light layer to draw the darkness where the server stops sending bodies; and the valley fog's
   * rules (REL-10: `WorldMap.valleyFogSightAt` with the moment of the day), for the views to draw its
   * bank and fade the bodies it hides as the server judges them.
   */
  concealment: z.object({
    nightSight: z.number().positive(),
    night: z.boolean(),
    valley: z.object({ on: z.boolean(), settleMs: z.number().int().positive() }),
  }),
  /**
   * The rules of a lot (HOME-CLAIM): with them the client previews a lot exactly like the server
   * judges it (`lotRefusal`, `lotPrice`, `proposeLot`); the server still decides.
   */
  lots: z.object({
    minSide: z.number().int().positive(),
    maxSide: z.number().int().positive(),
    maxArea: z.number().int().positive(),
    defaultSide: z.number().int().positive(),
    roadDistance: z.number().int().nonnegative(),
    entranceDistance: z.number().int().nonnegative(),
    separation: z.number().int().nonnegative(),
    price: z.object({
      perTile: z.number().positive(),
      town: z.object({ bonus: z.number().nonnegative(), reach: z.number().int().positive() }),
      road: z.object({ bonus: z.number().nonnegative(), reach: z.number().int().positive() }),
    }),
    /** The canon of a paid lot (plan C7): coins a game day per tile, and its terms. */
    rent: z.object({
      perTile: z.number().positive(),
      firstDays: z.number().int().positive(),
      aheadDays: z.number().int().positive(),
      warnMs: z.number().int().nonnegative(),
      graceMs: z.number().int().nonnegative(),
    }),
    /** Building without leave (plan C7): when it is noticed, pulled down, and the fine. */
    squat: z.object({
      noticeMs: z.number().int().nonnegative(),
      graceMs: z.number().int().nonnegative(),
      fine: z.number().nonnegative(),
    }),
    /** How much dearer each further lot of a company grows (plan E6, LC-4). */
    company: z.object({ growth: z.number().nonnegative() }),
    /** How long a ruin waits for its former owner before anyone reclaims it (RC-1). */
    ruin: z.object({ claimAfterMs: z.number().int().nonnegative() }),
    /** What a home costs to keep (EC-R2): coins a game day of a lot with its hearth lit. */
    hearthUpkeep: z.number().nonnegative(),
    /** Durability (DR-1..7): how pieces decay unkept, so the client shows their condition. */
    durability: z.object({
      decayMs: z.number().int().positive(),
      integrityFloor: z.number().gt(0).max(1),
      hearthRuinAfterMs: z.number().int().nonnegative(),
    }),
    /** The sleeping house (OP-1): how long all its people are away before a lot sleeps. */
    sleep: z.object({ afterMs: z.number().int().positive() }),
  }),
  /**
   * What cancelling a project and dismantling a structure give back (BUILD technique 9, plan
   * E13): shares of what was carried and of the cost, rounded down, so the client shows the
   * refund before asking.
   */
  construction: z.object({
    cancelRefund: z.number().min(0).max(1),
    dismantleRefund: z.number().min(0).max(1),
  }),
  /**
   * Each small site's right and daily upkeep (plan C10, SS-4), by place, so the client shows them
   * before a claim; the server still charges them.
   */
  sites: z.object({
    places: z.record(
      z.string(),
      z.object({ right: z.number().int().nonnegative(), upkeep: z.number().nonnegative() }),
    ),
  }),
  /**
   * The inns (HOME, plan C2): each one's door in world tiles and whether its rooms are safe (a
   * sanctuary's, RV-14), with the rent's rules.
   */
  inns: z.array(
    z.object({
      id: z.string(),
      x: z.number().int(),
      y: z.number().int(),
      safe: z.boolean(),
    }),
  ),
  /**
   * Swimming (MOVE-SWIM T11): the light it asks, for the views to tell why one cannot swim (the
   * server decides, `swims` in `pace`): the share of the free load, the weapon families, a shield.
   */
  swim: z.object({
    loadShare: z.number().min(0).max(1),
    weapons: z.array(z.string()),
    shield: z.boolean(),
  }),
  /**
   * Ships (MOVE-SHIP): what a helmsman's client steps its ship by (T3: the current's strength and
   * each class's hull), each class's deck and hold for the views, and the layout's docks, where
   * ships moor, load and are bought (T12). The server still judges.
   */
  ships: z
    .object({
      current: z.number().min(0),
      dockReach: z.number().positive(),
      /** Raising a sunk ship at its wreck (CW-10 a): a share of its price, what its client asks first. */
      refloat: z.object({ share: z.number().min(0).max(1) }),
      classes: z.record(
        z.string(),
        z.object({
          speed: z.number().positive(),
          astern: z.number().min(0).max(1),
          accel: z.number().positive(),
          turn: z.number().positive(),
          radius: z.number().positive(),
          draft: z.enum(['deep', 'shallow']),
          seats: z.number().int().positive(),
          /** Its crew (CW-1, CW-4): the helmsman's client counts hands as the shard does. */
          crew: z.object({
            min: z.number().int().positive(),
            handSpeed: z.number().min(0),
            maxHands: z.number().int().min(0),
          }),
          deck: z.number().min(0),
          hold: z.number().int().min(0),
          integrity: z.number().int().positive(),
          price: z.number().int().positive(),
        }),
      ),
      docks: z.array(
        z.object({
          id: z.string(),
          x: z.number(),
          y: z.number(),
          place: z.string(),
          boatman: z.string().optional(),
        }),
      ),
    })
    .optional(),
  /** The auction houses (ECON-MARKET step 2, MK-6): each one's door in world tiles. */
  auctionHouses: z.array(z.object({ id: z.string(), x: z.number().int(), y: z.number().int() })),
  /** How near a house's door its goods are listed and taken out, in metres. */
  auctionReach: z.number().positive(),
  /**
   * The Depths' mouths (DEPTH-ENTRANCE): how near an end a descent starts, in metres, and how long
   * it takes (the mouths themselves are the layout's, `WorldMap.depths`).
   */
  depths: z.object({ reach: z.number().positive(), descendMs: z.number().int().nonnegative() }),
  /** How near a merchant's door one trades with it (C9, NP-1): `market.reach`. */
  shopReach: z.number().positive(),
  /** The hiring halls (PROD-WORKER): each one's door in world tiles, and how near one hires. */
  hiringHalls: z.array(z.object({ id: z.string(), x: z.number().int(), y: z.number().int() })),
  hiringReach: z.number().positive(),
  /** How near a quest giver one stands to speak to it, in metres (QUEST, `quests.reach`). */
  questReach: z.number().positive(),
  /** How near a passing merchant one stands to buy, in metres (IE-10, `worldEvents.merchant.reach`). */
  merchantReach: z.number().positive(),
  /**
   * How carried load slows a character (MOVE-CHARACTER, `movement.load`): the backpack's bar marks
   * where it turns heavy (`freeShare` of its capacity) and how slow it gets.
   */
  loadRules: z.object({
    freeShare: z.number().min(0).lt(1),
    slowest: z.number().positive().max(1),
    overloadShare: z.number().min(0).max(2),
    overloadSlowest: z.number().positive().max(1),
  }),
  /**
   * Each creature sheet's parent layer (its `extends`), so the client draws a sheet it has no
   * look for as the nearest layer above it that has one (HB-1: every place its own sheets).
   */
  creatureBases: z.record(z.string(), z.string()),
  /** The quest family's items (QUEST): never sold, weightless, never lost; the client marks them. */
  questItems: z.array(ItemIdSchema),
  /**
   * The items one reads (ITEM-READ): their words are `itemTexts.<id>`; `teaches` when reading one
   * asks the server (`readItem`) to learn the places it names.
   */
  readables: z.array(z.object({ item: ItemIdSchema, teaches: z.boolean() })).optional(),
  /** The marks' catalog (CHAR-MARK): an entity's `marks` index it; names are `marks.<id>`. */
  marks: z.array(z.string()).optional(),
  /**
   * How near a character stands to talk to a townsperson, and to overhear one (NPC-RUMOR, NR-8),
   * in metres: the client offers talking and asks for what it overhears within them.
   */
  rumors: z
    .object({
      reach: z.number().positive(),
      overhear: z.number().positive(),
      /**
       * The most a Wanderer asks for the news of one's residue, by how sharp it is told (PF-4,
       * RN-4): what is charged is in its answer, never more (RN-6).
       */
      news: z
        .object({
          exact: z.number().int().nonnegative(),
          near: z.number().int().nonnegative(),
          far: z.number().int().nonnegative(),
        })
        .optional(),
    })
    .optional(),
  /**
   * The items worn, with the slot each takes (EQUIP-SLOT): only these are offered to equip; a tool
   * or a document is carried, never worn (R-29, B148).
   */
  wornItems: z.record(ItemIdSchema, z.string()),
  /** The Grimoire's rules (MAGIC-GRIMOIRE §11.2): for `affinityOf` and what a major spell asks. */
  grimoireRules: z.object({
    affinityCap: z.number().int().positive(),
    affinityPerGrade: z.number().nonnegative(),
    majorAffinity: z.number().int().nonnegative(),
    /** A master's price and the standing it asks, by a spell's level (MA-1, MA-2). */
    lessonPrice: z.object({ minor: z.number(), moderate: z.number(), major: z.number() }),
    lessonBand: z.object({
      minor: ReputationBandSchema,
      moderate: ReputationBandSchema,
      major: ReputationBandSchema,
    }),
    /** How near a master to stand to learn from it, in metres (MA-3). */
    teacherReach: z.number().positive(),
    /** The Intelligence a major spell asks to be learned (GS-2). */
    majorIntelligence: z.number().int().nonnegative(),
  }),
  /** The tomes (MAGIC-GRIMOIRE): each one's spell, read with `learnFromTome`. */
  tomes: z.record(ItemIdSchema, z.string()),
  /**
   * How each kind of flock flies (CREATURE-FLIER), for `birdAt`: the client draws the layout's
   * flocks (`flocksOf`) where the server judges them; `flocks` messages tell scares and falls.
   */
  flocks: z.object({
    renewMs: z.number().int().positive(),
    kinds: z.record(
      z.string(),
      z.object({
        birds: z.number().int().positive(),
        circle: z.number().positive(),
        periodMs: z.number().int().positive(),
        height: z.number().positive(),
        alert: z.number().positive(),
        flee: z.number().positive(),
        fledHeight: z.number().positive(),
        settleMs: z.number().int().positive(),
        radius: z.number().positive(),
      }),
    ),
  }),
  /**
   * What each piece of gear does, for its sheet (B160): a weapon's family and the damage of its
   * chain's first blow at its quality, before level and mastery (in health tenths, PW-2); an
   * armor piece's protection, against projectiles too, stability and pace; a tool's kind, tier
   * and durability; an off-hand's guard and amplifier. Only items that have one.
   */
  gear: z.record(
    ItemIdSchema,
    z.object({
      weapon: z
        .object({
          family: z.string(),
          damage: z.number().int().nonnegative(),
          quality: z.number(),
        })
        .optional(),
      armor: z
        .object({
          protection: z.number().int().nonnegative(),
          projectile: z.number().int().nonnegative(),
          stability: z.number().int().nonnegative(),
          pace: z.number(),
        })
        .optional(),
      tool: z
        .object({
          kind: z.string(),
          tier: z.number().int().positive(),
          durability: z.number().int().positive(),
        })
        .optional(),
      offhand: z.object({ guards: z.boolean(), amplify: z.number().optional() }).optional(),
    }),
  ),
  /**
   * Gathering by blows (INPUT-INTERACT, B147): the tool kind each node kind asks (null: taken by
   * hand, with F, IX-7), and a blow's cycle by tool kind, so a click never sends blows sooner
   * than the server counts them. The server still judges every blow. `instant` (RES-GATHER RG-16):
   * the node kinds taken at once by hand, within `reach` metres of their edge, one per `cycleMs`.
   */
  strikes: z.object({
    nodeTools: z.record(z.string(), z.string().nullable()),
    cycleMs: z.record(z.string(), z.number().int().positive()),
    handMs: z.number().int().positive(),
    instant: z.object({
      kinds: z.array(z.string()),
      reach: z.number().positive(),
      cycleMs: z.number().int().positive(),
    }),
  }),
  /**
   * The traced place each living community lives at (LL-4), by its id: an id is not always its
   * place (the Crown's post lives at the burnt post). For the map's mark.
   */
  communityPlaces: z.record(z.string(), z.string()),
  /** The side of a ground cell, in metres (LL-1), so the client lays the cells it paints. */
  groundCellMetres: z.number().int().positive(),
  /** The sheets of the wounded travelers (WORLD-ENCOUNTER EN-3): F speaks to them. */
  travelers: z.array(z.string()),
  /** What each sheet that takes a toll asks (VB-1), for its notice: its price, its power. */
  tolls: z.record(z.string(), z.object({ price: z.number().int().positive(), power: z.string() })),
  lodging: z.object({
    rentPerDay: z.number().int().positive(),
    maxDays: z.number().int().positive(),
    /** What a room first rented holds: its first size's. */
    capacity: z.number().int().positive(),
    /** A room's sizes (HOME TI-3): what each holds and its rent's factor; the last the cap. */
    sizes: z
      .array(z.object({ capacity: z.number().int().positive(), rentFactor: z.number().min(1) }))
      .min(1),
    reach: z.number().positive(),
  }),
  /**
   * The companies' rules (B105): what founding costs, how many fit, how long before
   * withdrawing, and what each rank may do, for the panel to offer only what is allowed.
   */
  companies: z.object({
    foundingCost: z.number().int().positive(),
    maxMembers: z.number().int().positive(),
    withdrawSeniorityMs: z.number().int().nonnegative(),
    ranks: z.record(
      CompanyRankSchema,
      z.object({
        invite: z.boolean(),
        expel: z.boolean(),
        setRank: z.boolean(),
        withdraw: z.boolean(),
        /** On the company's lots (plan E6): build, take from storages, open locks, claim. */
        build: z.boolean(),
        store: z.boolean(),
        enter: z.boolean(),
        claim: z.boolean(),
        /** Undo and take back on its lots (LC-7); leave one to ruin (LC-9). */
        dismantle: z.boolean(),
        leave: z.boolean(),
      }),
    ),
  }),
  /** Every fusion (MAGIC-FUSION): the fragment it makes, by the fragments it consumes. */
  fusions: z.record(ItemIdSchema, z.object({ inputs: z.array(ItemIdSchema) })),
  /**
   * What each weapon family gives the skill slots (WEAPON-TECHNIQUE): its techniques with the
   * mastery rank that opens each (Q, E), and whether it casts (R to X with magic).
   */
  weaponSkills: z.record(
    z.string(),
    z.object({
      techniques: z.array(
        z.object({ technique: z.string(), rank: z.number().int().nonnegative() }),
      ),
      casts: z.boolean(),
    }),
  ),
  /**
   * Every technique (MAGIC-TECHNIQUE): what it costs and how long it recovers, for the HUD to pace
   * them; the server still decides each use.
   */
  techniques: z.record(
    z.string(),
    z.object({
      focus: z.number().int().nonnegative(),
      recoveryMs: z.number().int().nonnegative(),
      windupMs: z.number().int().nonnegative(),
      kind: z.enum([
        'dash',
        'barrier',
        'strike',
        'bolt',
        'zone',
        'heal',
        'ward',
        'wall',
        'gust',
        'channel',
      ]),
      /** Magic (MU-8, WQ-3): cast with a wand or a staff from R to X, paying Focus. */
      magic: z.boolean(),
      /** Its school (MAGIC-GRIMOIRE §11.1), for the slot and the affinity it grows by. */
      school: z.string().optional(),
      /** A spell's level (MAGIC-GRIMOIRE §3): minor when absent. */
      tier: z.enum(['minor', 'moderate', 'major']).optional(),
      /** A dash's pace factor and length, for the owner to predict it like the server. */
      dash: z.object({ factor: z.number(), durationMs: z.number().int().positive() }).optional(),
      /** A channel's longest hold (TECHNIQUE-BATCH-C): its slot again lets it go meanwhile. */
      channel: z.object({ durationMs: z.number().int().positive() }).optional(),
      /** The group it recovers with (DB-102 d), for `recoveryKeyOf` to predict like the server. */
      shares: z.string().optional(),
    }),
  ),
});
export type Catalog = z.infer<typeof CatalogSchema>;

/**
 * A server's catalog by its hash (NET-CLIENT-CACHE C2): immutable, cached a year by anyone. Asked
 * with another hash than the server's now it is `not_found`: the client asks for the one its
 * `welcome` names.
 */
export const getCatalog = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/catalog/:hash`,
  access: 'none',
  params: z.object({ hash: CatalogHashSchema }),
  response: CatalogSchema,
});

/**
 * What the character needs to render after sign-in or reconnection: its own state and the world's
 * now, with the hash of the catalog that goes with it (NET-CLIENT-CACHE C2). It carries an ETag over
 * all of it but `serverTime`: asked again with `If-None-Match` and nothing changed, it answers
 * `304` and nothing travels (the client takes the game time from its `welcome`).
 */
export const getInitialState = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/me/state`,
  access: 'session',
  response: z.object({
    serverTime: TimestampMsSchema,
    protocolVersion: z.literal(PROTOCOL_VERSION),
    /** The world layout the server lays (ADR 0060): the client's map lays the same. */
    layout: WorldLayoutIdSchema,
    player: PlayerSchema,
    /** The character's equipment (weapon tier from the workshop; 0 = basic weapon). */
    equipment: z.object({ weaponLevel: z.number().int().nonnegative() }),
    /** The character's item bar (ITEM-CONSUME): the item of each slot, 1 to 8 (null: empty). */
    hotbar: HotbarSchema,
    /**
     * The fixed treasures it found already (WORLD-TREASURE T1): its client leaves them out; where
     * they lie is the layout's (`treasuresOf`), and the hidden ones near it come from the shard.
     */
    treasures: z.object({
      found: z.array(z.string()),
      openMs: z.number().int().positive(),
    }),
    /** Its Tone (DEATH-PULSE §4) and what its client reads it with (`toneAt`, the grades). */
    tone: ToneStateSchema,
    /** The raids on its homes it was not told of yet (LOCAL-LIFE LL-5), oldest first. */
    raids: z.array(LotRaidSchema),
    /** The coalition standing against its faction and its threat's level (NPC-KINGDOM-REACT). */
    coalition: CoalitionStateSchema.optional(),
    /**
     * The day's rules (WORLD-DAYNIGHT): with `serverTime` (game time) the client computes the
     * moment of the day exactly like the server (`dayTimeAt`). Here and not in `welcome`, because
     * this answer carries game time (the dev clock moves it) and the gateway has no game clock.
     */
    day: z.object({
      originMs: z.number().int().nonnegative(),
      dawnMs: z.number().int().positive(),
      dayMs: z.number().int().positive(),
      duskMs: z.number().int().positive(),
      nightMs: z.number().int().positive(),
    }),
    /**
     * The weather's rules (WORLD-CLIMATE): with the game time the client computes each region's
     * weather and the next span's exactly like the server (`weatherAt`).
     */
    weather: z.object({
      originMs: z.number().int().nonnegative(),
      spanMs: z.number().int().positive(),
      kinds: z.record(
        z.string(),
        z.object({
          exposure: z
            .object({
              state: z.string(),
              strength: z.number(),
              ms: z.number().int(),
              everyMs: z.number().int(),
            })
            .optional(),
          sight: z.number().positive().optional(),
          nightFactor: z.number().positive().optional(),
          lightFactor: z.number().positive().optional(),
        }),
      ),
      profiles: z.record(
        z.string(),
        z.object({
          weights: z.record(z.string(), z.number().int().positive()),
          stayPercent: z.number().int(),
        }),
      ),
      regions: z.record(z.string(), z.string()),
      defaultProfile: z.string(),
      forced: z.record(z.string(), z.string()).optional(),
    }),
    /** The living communities fallen now (LL-4b); later falls and recoveries come as `community`. */
    fallenCommunities: z.array(z.string()),
    /** The announced world events open now (EV-4); later ones come as `worldEvent`. */
    worldEvents: z.array(WorldEventViewSchema),
    /** Who holds each traced region's ground now (LL-1), cell by cell; later days come as `ground`. */
    ground: z.record(z.string(), z.array(z.string().nullable())),
    /**
     * The Pulse's rules (WORLD-PULSE): with the game time the client computes each region's phase,
     * its countdown and its warning exactly like the server (`pulseAt`), with no message.
     */
    pulse: z.object({
      originMs: z.number().int().nonnegative(),
      cycleMs: z.number().int().positive(),
      agitationMs: z.number().int().positive(),
      intenseMs: z.number().int().positive(),
      warnMs: z.number().int().nonnegative(),
      major: z.object({
        everyCycles: z.number().int().positive(),
        ms: z.number().int().positive(),
      }),
      intensity: z.record(z.string(), z.number().positive()),
      forced: z.record(z.string(), z.enum(['calm', 'agitation', 'intense'])).optional(),
      /** Regions whose Major Pulse is forced (H2c, the dev tools). */
      forcedMajor: z.record(z.string(), z.literal(true)).optional(),
      /** The Intense Pulses brought forward (27.6, PA-1..3), by region and cycle: their birth. */
      advances: z.record(z.string(), z.record(z.string(), z.number().int())).optional(),
    }),
    /** The catalog that goes with it (`getCatalog`). */
    catalog: CatalogHashSchema,
  }),
});

/** The initial state with its catalog: what the client renders from (NET-CLIENT-CACHE C2). */
export type InitialState = z.infer<(typeof getInitialState)['response']> & Catalog;
